import {
  Actions,
  Attribute,
  PlayerRoundState,
  PlayerTraits,
  Zones,
} from '@alienheadwars/football-card-manager-model';
import { useState } from 'react';
import { getCallableFunction } from '../../../../../firebase/firebase';
import { localise } from '../../../../../locale/locale';
import { ButtonComponent, Type } from '../../../../base/button/Button';
import { Icon } from '../../../../base/icon/Icon';
import { HelpOverlay } from '../../../../base/tutorial/helpOverlay/HelpOverlay';
import {
  getAttackingAttributes,
  getDefensiveAttributes,
} from '../../../../cards/playerCards/AttributeSets';
import { SelectablePlayerCard } from '../../../../cards/playerCards/SelectablePlayerCard';
import {
  AnimatedBox,
  AnimatedCard,
  AnimatedStack,
} from '../../../../MotionMui/MotionMui';
import { useMatchViewState } from '../matchViewContext/MatchViewContext';

export const PlayerChoiceComponent = () => {
  const matchViewState = useMatchViewState();
  const currentRound = matchViewState.currentRound;
  if (currentRound.roundFinished) {
    //Early out if round is finished
    return <></>;
  }
  const playerIsAttacking =
    matchViewState.currentRound.attackingTeam ===
    matchViewState.matchState.homeTeam;
  if (playerIsAttacking) {
    if (currentRound.attackingPlayer === undefined)
      return <ChooseKickOffPlayer />;
    if (currentRound.action === undefined)
      return <ChooseAttackingTargetPlayerAndAction />;
  } else {
    if (currentRound.defendingPlayer === undefined)
      return <ChooseDefendingPlayer />;
  }
  return <></>;
};

export const ChooseAttackingTargetPlayerAndAction = () => {
  const matchViewState = useMatchViewState();
  const [targetPlayer, setTargetPlayer] = useState(
    undefined as unknown as number
  );
  const [loading, setLoading] = useState(false);

  const chooseAction = getCallableFunction('actionMatch');
  const chooseActionAction = (action: Actions) => {
    const params = {
      matchId: matchViewState.matchState.id,
      gameAction: {
        action: action,
        attackingTargetPlayer: targetPlayer,
      },
    };
    chooseAction(params);
    setLoading(true);
    matchViewState.updateCurrentRound({
      ...matchViewState.currentRound,
      action: action,
      attackingTargetPlayer: targetPlayer,
    });
  };

  const showShortPass =
    matchViewState.currentRound.attackingHand[
      matchViewState.currentRound.attackingPlayer!
    ].playerDetails.combinedBonus.playerTraits.includes(
      PlayerTraits.SHORT_PASS
    ) ||
    (targetPlayer !== undefined &&
      matchViewState.currentRound.attackingHand[
        targetPlayer
      ].playerDetails.combinedBonus.playerTraits.includes(
        PlayerTraits.SHORT_PASS
      ));

  return (
    <AnimatedCard staggerSpeed={'Fast'} sx={{}}>
      <HelpOverlay
        additionalCondition={targetPlayer === undefined}
        helpText={localise('match.set.attackingTargetPlayer.help')}
        tutorialId="match.set.attackingTargetPlayer.help"
        blockSkip={true}
      >
        <ChoosePlayer
          filter={(player, index) =>
            !player.discarded &&
            index !== matchViewState.currentRound.attackingPlayer &&
            index !== 0
          }
          hand={matchViewState.currentRound.attackingHand}
          callback={(playerIndex: number) => setTargetPlayer(playerIndex)}
          currentlySelectedPlayer={targetPlayer}
          loading={loading}
          attributeSet={getAttackingAttributes(
            matchViewState.currentRound.zone
          )}
        />
      </HelpOverlay>
      <AnimatedStack
        staggerSpeed={'Fast'}
        direction="row"
        sx={{
          margin: '1vh',
          padding: '1vw',
        }}
        spacing={1}
      >
        <HelpOverlay
          additionalCondition={
            targetPlayer !== undefined &&
            !loading &&
            matchViewState.currentRound.roundId !== 0
          }
          helpText={localise('match.attackStall.help')}
          tutorialId="match.attackStall.help"
        >
          <ButtonComponent
            loadOnExecute
            disabled={loading}
            type={Type.SECONDARY}
            onClick={() => chooseActionAction(Actions.STALL)}
          >
            <Icon iconId="STALL" /> {localise('match.attackStall')}
          </ButtonComponent>
        </HelpOverlay>
        <HelpOverlay
          additionalCondition={targetPlayer !== undefined && !loading}
          helpText={localise('match.run.help')}
          tutorialId="match.run.help"
        >
          <ButtonComponent
            loadOnExecute
            disabled={targetPlayer === undefined || loading}
            onClick={() => chooseActionAction(Actions.RUN)}
          >
            <Icon iconId="RUN" /> {localise('match.run')}
          </ButtonComponent>
        </HelpOverlay>

        {
          //Don't offer pass if we can short pass and we're in attacking zone, there's no benefit and too many options
          (!showShortPass ||
            matchViewState.currentRound.zone !== Zones.ATTACK) && (
            <HelpOverlay
              additionalCondition={targetPlayer !== undefined && !loading}
              helpText={localise('match.pass.help')}
              tutorialId="match.pass.help"
            >
              <ButtonComponent
                loadOnExecute
                disabled={targetPlayer === undefined || loading}
                onClick={() => chooseActionAction(Actions.PASS)}
              >
                <Icon iconId="PASS" /> {localise('match.pass')}
              </ButtonComponent>
            </HelpOverlay>
          )
        }
        {
          //If either the player, or the target player, have short pass trait, the user may choose short pass option
          showShortPass && (
            <HelpOverlay
              additionalCondition={targetPlayer !== undefined && !loading}
              helpText={localise('match.short_pass.help')}
              tutorialId="match.short_pass.help"
            >
              <ButtonComponent
                loadOnExecute
                disabled={targetPlayer === undefined || loading}
                onClick={() => chooseActionAction(Actions.SHORT_PASS)}
              >
                <Icon iconId="PASS" /> {localise('match.short_pass')}
              </ButtonComponent>
            </HelpOverlay>
          )
        }
        {
          //If the zone is defense and either the player has long pass trait, or the target playerhas target trait, the user may choose long pass option
          matchViewState.currentRound.zone === Zones.DEFENSE &&
            (matchViewState.currentRound.attackingHand[
              matchViewState.currentRound.attackingPlayer!
            ].playerDetails.combinedBonus.playerTraits.includes(
              PlayerTraits.LONG_BALL
            ) ||
              (targetPlayer !== undefined &&
                matchViewState.currentRound.attackingHand[
                  targetPlayer
                ].playerDetails.combinedBonus.playerTraits.includes(
                  PlayerTraits.TARGET
                ))) && (
              <HelpOverlay
                additionalCondition={targetPlayer !== undefined && !loading}
                helpText={localise('match.long_ball.help')}
                tutorialId="match.long_ball.help"
              >
                <ButtonComponent
                  loadOnExecute
                  disabled={targetPlayer === undefined || loading}
                  onClick={() => chooseActionAction(Actions.LONG_PASS)}
                >
                  <Icon iconId="PASS" /> {localise('match.long_ball')}
                </ButtonComponent>
              </HelpOverlay>
            )
        }

        {matchViewState.currentRound.zone === Zones.ATTACK && (
          <HelpOverlay
            additionalCondition={targetPlayer !== undefined && !loading}
            helpText={localise('match.shoot.help')}
            tutorialId="match.shoot.help"
          >
            <ButtonComponent
              loadOnExecute
              disabled={loading}
              onClick={() => chooseActionAction(Actions.SHOOT)}
            >
              <Icon iconId="SHOOT" /> {localise('match.shoot')}
            </ButtonComponent>
          </HelpOverlay>
        )}
      </AnimatedStack>
    </AnimatedCard>
  );
};

export const ChooseDefendingPlayer = () => {
  const matchViewState = useMatchViewState();
  const [defendingPlayer, setDefendingPlayer] = useState(
    undefined as unknown as number
  );
  const [loading, setLoading] = useState(false);
  const chooseAction = getCallableFunction('actionMatch');
  const chooseDefender = (defendingPlayerP: number) => {
    const params = {
      matchId: matchViewState.matchState.id,
      gameAction: {
        defendingPlayer: defendingPlayerP,
      },
    };
    chooseAction(params);
    setLoading(true);
    matchViewState.updateCurrentRound({
      ...matchViewState.currentRound,
      defendingPlayer: defendingPlayerP,
    });
  };

  return (
    <AnimatedCard staggerSpeed={'Fast'} sx={{}}>
      <HelpOverlay
        additionalCondition={defendingPlayer === undefined}
        helpText={localise('match.set.defendingPlayer.help')}
        tutorialId="match.defendingPlayer.help"
        blockSkip={true}
      >
        <ChoosePlayer
          filter={(player, index) => !player.discarded && index !== 0}
          hand={matchViewState.currentRound.defendingHand}
          callback={(playerIndex: number) => setDefendingPlayer(playerIndex)}
          currentlySelectedPlayer={defendingPlayer}
          loading={loading}
          attributeSet={getDefensiveAttributes(
            matchViewState.currentRound.zone
          )} // Pass dynamically generated set
        />
      </HelpOverlay>
      <AnimatedStack staggerSpeed={'Fast'} direction="row" sx={{}} spacing={1}>
        <HelpOverlay
          additionalCondition={defendingPlayer !== undefined && !loading}
          helpText={localise('match.defendStall.help')}
          tutorialId="match.defendStall.help"
        >
          <ButtonComponent
            loadOnExecute
            disabled={loading}
            type={Type.SECONDARY}
            onClick={() => chooseDefender(-1)}
          >
            <Icon iconId="STALL" /> {localise('match.defendStall')}
          </ButtonComponent>
        </HelpOverlay>
        <HelpOverlay
          additionalCondition={defendingPlayer !== undefined && !loading}
          helpText={localise('match.contest.help')}
          tutorialId="match.contest.help"
        >
          <ButtonComponent
            loadOnExecute
            disabled={defendingPlayer === undefined || loading}
            onClick={() => chooseDefender(defendingPlayer)}
          >
            <Icon iconId="CONTEST" /> {localise('match.contest')}{' '}
          </ButtonComponent>
        </HelpOverlay>
      </AnimatedStack>
    </AnimatedCard>
  );
};

export const ChooseKickOffPlayer = () => {
  const matchViewState = useMatchViewState();
  const [attackingPlayer, setAttackingPlayer] = useState(
    undefined as unknown as number
  );
  const [loading, setLoading] = useState(false);

  const chooseAction = getCallableFunction('actionMatch');
  const chooseAttacker = () => {
    const params = {
      matchId: matchViewState.matchState.id,
      gameAction: {
        attackingPlayer: attackingPlayer,
      },
    };
    chooseAction(params);
    setLoading(true);
    matchViewState.updateCurrentRound({
      ...matchViewState.currentRound,
      attackingPlayer: attackingPlayer,
    });
  };

  return (
    <AnimatedCard staggerSpeed={'Fast'} sx={{}}>
      <HelpOverlay
        additionalCondition={attackingPlayer === undefined}
        helpText={localise('match.set.attackingPlayer.help')}
        tutorialId="match.set.attackingPlayer"
        blockSkip={true}
      >
        <ChoosePlayer
          filter={(player) => !player.discarded}
          hand={matchViewState.currentRound.attackingHand}
          callback={(playerIndex: number) => setAttackingPlayer(playerIndex)}
          currentlySelectedPlayer={attackingPlayer}
          loading={loading}
          attributeSet={getAttackingAttributes(
            matchViewState.currentRound.zone
          )} // Pass dynamically generated set
        />
      </HelpOverlay>
      <AnimatedStack staggerSpeed={'Fast'} direction="row" sx={{}} spacing={1}>
        <HelpOverlay
          additionalCondition={attackingPlayer !== undefined && !loading}
          helpText={localise('match.kickOff.help')}
          tutorialId="match.kickOff.help"
        >
          <ButtonComponent
            loadOnExecute
            disabled={attackingPlayer === undefined || loading}
            onClick={() => chooseAttacker()}
          >
            <Icon iconId="PASS" /> {localise('match.kickOff')}
          </ButtonComponent>
        </HelpOverlay>
      </AnimatedStack>
    </AnimatedCard>
  );
};

interface ChoosePlayerProps {
  filter: (player: PlayerRoundState, index: number) => boolean;
  hand: PlayerRoundState[];
  callback(playerIndex: number): void;
  currentlySelectedPlayer?: number;
  loading: boolean;
  attributeSet: Attribute[];
}

const ChoosePlayer = (props: ChoosePlayerProps) => {
  const players = props.hand
    .map((player, index) => {
      if (!props.filter(player, index)) {
        return undefined;
      }
      const selected: boolean = props.currentlySelectedPlayer === index;
      const playerKey = player.playerDetails?.id ?? index;

      return (
        <AnimatedBox key={playerKey}>
          <SelectablePlayerCard
            player={player.playerDetails}
            selected={selected}
            onClick={() => {
              if (!props.loading) {
                return props.callback(index);
              }
            }}
            attributeSet={props.attributeSet}
          />
        </AnimatedBox>
      );
    })
    .filter((player) => player !== undefined);
  return (
    <AnimatedStack
      staggerSpeed={'VeryFast'}
      direction="row"
      sx={{
        overflowX: 'scroll',
        filter: props.loading ? 'grayscale(75%)' : 'none',
        maxWidth: '90vw',
      }}
    >
      {players}
    </AnimatedStack>
  );
};
